import React from 'react';
import { cn } from '@utils/cn';
import { StockBadge, StockStatus, calculateStockStatus } from '@components/atoms/StockBadge';

interface StockLevelIndicatorProps {
  quantityOnHand: number;
  quantityReserved?: number;
  reorderPoint?: number;
  unit?: string;
  showBar?: boolean;
  showBadge?: boolean;
  showDetails?: boolean;
  maxQuantity?: number; // For percentage calculation
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const StockLevelIndicator: React.FC<StockLevelIndicatorProps> = ({
  quantityOnHand,
  quantityReserved = 0,
  reorderPoint,
  unit = 'units',
  showBar = true,
  showBadge = true,
  showDetails = true,
  maxQuantity,
  size = 'md',
  className,
}) => {
  const available = quantityOnHand - quantityReserved;
  const status = calculateStockStatus(available, reorderPoint);

  // Calculate percentage for bar
  const effectiveMax = maxQuantity || Math.max(quantityOnHand, reorderPoint || 0) * 1.5 || 100;
  const percentage = Math.min((quantityOnHand / effectiveMax) * 100, 100);
  const reservedPercentage = Math.min((quantityReserved / effectiveMax) * 100, 100);

  const sizeClasses = {
    sm: { bar: 'h-1.5', text: 'text-xs', gap: 'gap-1' },
    md: { bar: 'h-2', text: 'text-sm', gap: 'gap-2' },
    lg: { bar: 'h-3', text: 'text-base', gap: 'gap-3' },
  };

  const statusColors: Record<StockStatus, { bar: string; reorder: string }> = {
    in_stock: {
      bar: 'bg-green-500 dark:bg-green-400',
      reorder: 'border-green-300 dark:border-green-700',
    },
    low_stock: {
      bar: 'bg-yellow-500 dark:bg-yellow-400',
      reorder: 'border-yellow-300 dark:border-yellow-700',
    },
    out_of_stock: {
      bar: 'bg-red-500 dark:bg-red-400',
      reorder: 'border-red-300 dark:border-red-700',
    },
  };

  const colors = statusColors[status];
  const reorderLinePosition = reorderPoint
    ? Math.min((reorderPoint / effectiveMax) * 100, 100)
    : null;

  return (
    <div className={cn('w-full', className)}>
      {/* Header with details */}
      {showDetails && (
        <div
          className={cn(
            'flex items-center justify-between mb-1',
            sizeClasses[size].text,
            sizeClasses[size].gap,
          )}>
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {available.toLocaleString()} {unit}
            </span>
            <span className="text-gray-500 dark:text-gray-400">available</span>
          </div>
          {showBadge && <StockBadge status={status} size={size} />}
        </div>
      )}

      {/* Progress bar */}
      {showBar && (
        <div className="relative">
          {/* Background */}
          <div
            className={cn(
              'w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden',
              sizeClasses[size].bar,
            )}>
            {/* Reserved portion (lighter) */}
            {quantityReserved > 0 && (
              <div
                className={cn(
                  'absolute top-0 left-0 h-full opacity-40',
                  colors.bar,
                )}
                style={{ width: `${percentage}%` }}
              />
            )}
            {/* Available portion */}
            <div
              className={cn(
                'h-full rounded-full transition-all duration-300',
                colors.bar,
              )}
              style={{
                width: `${percentage - reservedPercentage}%`,
              }}
            />
          </div>

          {/* Reorder point marker */}
          {reorderLinePosition !== null && (
            <div
              className={cn(
                'absolute top-0 bottom-0 w-0.5 border-l-2 border-dashed',
                colors.reorder,
              )}
              style={{ left: `${reorderLinePosition}%` }}
              title={`Reorder point: ${reorderPoint} ${unit}`}
            />
          )}
        </div>
      )}

      {/* Breakdown details */}
      {showDetails && quantityReserved > 0 && (
        <div
          className={cn(
            'flex items-center justify-between mt-1 text-gray-500 dark:text-gray-400',
            sizeClasses[size].text,
          )}>
          <span>
            On hand: {quantityOnHand.toLocaleString()} {unit}
          </span>
          <span>
            Reserved: {quantityReserved.toLocaleString()} {unit}
          </span>
        </div>
      )}
    </div>
  );
};

export default StockLevelIndicator;
